import { ActionIcon, Group, Stack, Text, Tooltip } from '@mantine/core';
import { IconThumbUp, IconThumbDown } from '@tabler/icons-react';
import { AssistantMessageProps } from './types';
import * as classes from './styles/index.css';
import { useTranslation } from 'react-i18next';
import {
  AssistantMessageVote,
  useVoteAssistantMessageMutation,
} from '@app/types/blackroom-api';
import { gql, useFragment } from '@apollo/client';
import ThinkingFeed from '@features/AIAssistant/ThinkingFeed';
import SourcesBar from '@features/AIAssistant/SourcesBar';

const ASSISTANT_MESSAGE_FRAGMENT = gql`
  fragment AssistantMessageVote on AssistantMessage {
    id
    vote
  }
`;

function AssistantMessage({
  content,
  messageId,
  size = 'md',
  className,
  thinkingSteps,
  sources,
  isStreaming,
  onSourceClick,
}: AssistantMessageProps) {
  const { t } = useTranslation();

  const { data } = useFragment({
    fragment: ASSISTANT_MESSAGE_FRAGMENT,
    from: {
      __typename: 'AssistantMessage',
      id: messageId,
    },
  });

  const vote = data?.vote ?? null;

  const [voteAssistantMessage] = useVoteAssistantMessageMutation();

  const handleVote = async (clickedVote: AssistantMessageVote) => {
    const newVote = clickedVote === vote ? null : clickedVote;

    voteAssistantMessage({
      variables: {
        assistantMessageId: messageId,
        vote: newVote,
      },
      optimisticResponse: {
        voteAssistantMessage: {
          __typename: 'AssistantMessage',
          id: messageId,
          vote: newVote,
        },
      },
    });
  };

  return (
    <Stack gap={4} w="100%">
      {thinkingSteps && (thinkingSteps.length > 0 || isStreaming) && (
        <ThinkingFeed
          steps={thinkingSteps}
          isStreaming={isStreaming ?? false}
        />
      )}
      <Text component="div" size={size} className={className}>
        {content}
      </Text>
      {!isStreaming && sources && sources.length > 0 && onSourceClick && (
        <SourcesBar sources={sources} onSourceClick={onSourceClick} />
      )}
      {!isStreaming && (
        <Group gap="xs">
          {(vote === null || vote === AssistantMessageVote.Up) && (
            <Tooltip
              label={t('AIAssistant:Chat.goodResponse')}
              position="top"
              withArrow
              zIndex={201}
            >
              <ActionIcon
                variant={vote === AssistantMessageVote.Up ? 'filled' : 'subtle'}
                size="sm"
                onClick={() => handleVote(AssistantMessageVote.Up)}
                className={classes.voteButton}
                color={vote === AssistantMessageVote.Up ? 'blue' : 'gray'}
                aria-label={t('AIAssistant:Chat.goodResponse')}
              >
                <IconThumbUp size={18} />
              </ActionIcon>
            </Tooltip>
          )}
          {(vote === null || vote === AssistantMessageVote.Down) && (
            <Tooltip
              label={t('AIAssistant:Chat.badResponse')}
              position="top"
              withArrow
              zIndex={1002}
            >
              <ActionIcon
                variant={
                  vote === AssistantMessageVote.Down ? 'filled' : 'subtle'
                }
                size="sm"
                onClick={() => handleVote(AssistantMessageVote.Down)}
                className={classes.voteButton}
                color={vote === AssistantMessageVote.Down ? 'red' : 'gray'}
                aria-label={t('AIAssistant:Chat.badResponse')}
              >
                <IconThumbDown size={18} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      )}
    </Stack>
  );
}

export default AssistantMessage;
